import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, Package, Clock, MapPin, Phone, CheckCircle, ShieldAlert } from 'lucide-react';

export default function ShippingPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-primary text-white py-14 px-4 text-center">
        <p className="text-xs tracking-[0.3em] text-accent uppercase mb-3">Royal Pet Store Policies</p>
        <h1 className="text-3xl md:text-4xl font-serif font-black">Shipping & Transit Info</h1>
        <p className="text-gray-400 text-sm mt-3">Safe climate-controlled pet transport & premium courier delivery across Pakistan</p>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">

        {/* Shipping Cost Banner */}
        <div className="bg-gradient-to-r from-primary-900 to-primary rounded-2xl p-7 text-white text-center mb-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-40 h-40 bg-accent/10 rounded-full -translate-y-10 translate-x-10" />
          <Truck size={36} className="mx-auto mb-4 text-accent" />
          <h2 className="text-xl font-serif font-black uppercase tracking-widest mb-4">Delivery Charges</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white/10 rounded-xl p-5 border border-white/10">
              <div className="text-2xl font-black text-green-400 mb-1">FREE</div>
              <div className="text-sm text-gray-300 font-semibold">On orders above</div>
              <div className="text-xl font-black text-white mt-1">Rs. 10,000</div>
            </div>
            <div className="bg-white/10 rounded-xl p-5 border border-white/10">
              <div className="text-2xl font-black text-yellow-400 mb-1">Rs. 250</div>
              <div className="text-sm text-gray-300 font-semibold">On orders below</div>
              <div className="text-xl font-black text-white mt-1">Rs. 10,000</div>
            </div>
          </div>
          <p className="text-xs text-gray-400 mt-4">* Apply to all pet feeds, treats, and accessories. Pets adoptions have customized transit schedules.</p>
        </div>

        {/* Key Info Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
          {[
            { icon: Clock, title: 'Adoption Verification', desc: 'Pet adoptions require 1–2 business days for vet health clearance and space confirmation.' },
            { icon: Package, title: 'Courier Delivery Time', desc: 'Feeds and accessories are delivered in 3–5 business days for major cities. Remote areas: 5–7 days.' },
            { icon: MapPin, title: 'Safe Animal Transit', desc: 'Live pets are transported via our climate-controlled vans to লাহোর, Karachi, Islamabad, and key cities.' },
            { icon: CheckCircle, title: 'Transit Scheduling', desc: 'Our adoption coordinators coordinate directly with you to schedule the exact handover date & hour.' },
          ].map(item => (
            <div key={item.title} className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-100">
              <div className="w-10 h-10 bg-[#FAF8F5] border border-accent/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <item.icon size={18} className="text-accent" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Detailed Sections */}
        <div className="space-y-8">
          <div>
            <h2 className="text-base font-serif font-bold text-gray-900 mb-3 uppercase tracking-wide flex items-center gap-2">
              <ShieldAlert size={18} className="text-accent" />
              1. Live Pet Climate-Controlled Transit
            </h2>
            <div className="bg-yellow-50/50 rounded-xl p-6 border border-accent/10 space-y-3 text-sm text-gray-600">
              <p>
                <strong>Zero-Stress Guarantee:</strong> Live animals are never dispatched through standard courier companies. Royal Pet Store operates dedicated climate-regulated transport vans.
              </p>
              <p>
                <strong>Care on Board:</strong> Professional animal handlers feed, hydrate, and monitor the physical comfort of your adopted companion during transit. Handover is completed directly to the adopter after vaccine file review.
              </p>
            </div>
          </div>

          <div>
            <h2 className="text-base font-serif font-bold text-gray-900 mb-3 uppercase tracking-wide">2. Feed & Accessories Shipping Rates</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="px-5 py-3 text-left font-semibold tracking-wide">Order Total (Feeds & Accessories)</th>
                    <th className="px-5 py-3 text-left font-semibold tracking-wide">Courier Charge</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-gray-100">
                    <td className="px-5 py-3.5 text-gray-600">Below Rs. 10,000</td>
                    <td className="px-5 py-3.5 font-semibold text-gray-900">Rs. 250</td>
                  </tr>
                  <tr className="border-t border-gray-100 bg-green-50">
                    <td className="px-5 py-3.5 text-gray-600">Rs. 10,000 and above</td>
                    <td className="px-5 py-3.5 font-bold text-green-600">FREE</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h2 className="text-base font-serif font-bold text-gray-900 mb-3 uppercase tracking-wide">3. Transit Timeframes (Feeds & Accessories)</h2>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-primary text-white">
                    <th className="px-5 py-3 text-left font-semibold">Region</th>
                    <th className="px-5 py-3 text-left font-semibold">Estimated Time</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { region: 'Lahore (City)', time: '1–2 Business Days' },
                    { region: 'Karachi, Islamabad, Rawalpindi', time: '2–3 Business Days' },
                    { region: 'Other Major Cities', time: '3–5 Business Days' },
                    { region: 'Remote / Rural Areas', time: '5–7 Business Days' },
                  ].map((row, i) => (
                    <tr key={row.region} className={`border-t border-gray-100 ${i % 2 === 1 ? 'bg-gray-50' : ''}`}>
                      <td className="px-5 py-3.5 text-gray-600">{row.region}</td>
                      <td className="px-5 py-3.5 font-medium text-gray-900">{row.time}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-400 mt-2">* courier delivery times are estimates and may vary due to weather or public holidays.</p>
          </div>

          <div>
            <h2 className="text-base font-serif font-bold text-gray-900 mb-3 uppercase tracking-wide">4. Cash on Delivery (COD)</h2>
            <ul className="space-y-2">
              {[
                'Cash on Delivery is available for feeds, treats, and accessories across Pakistan.',
                'COD verification calls are placed before dispatching feeds and accessories.',
                'For live pet adoptions, a small verification deposit may be requested prior to dispatch.',
              ].map(item => (
                <li key={item} className="flex items-start gap-2 text-sm text-gray-600 leading-relaxed">
                  <span className="text-accent mt-1 flex-shrink-0">•</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="text-base font-serif font-bold text-gray-900 mb-3 uppercase tracking-wide">5. Courier Order Tracking (Feeds & Accessories)</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-3">
              Once your feeds or accessories are dispatched, you will receive an SMS and email with your tracking number. You can use this to track your parcel on our logistics partner's website.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              For any transit issues, please contact our support team and we will coordinate with the courier company immediately.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-[#FAF8F5] rounded-xl p-6 border border-accent/20 flex flex-col sm:flex-row items-center gap-4">
          <Phone size={28} className="text-accent flex-shrink-0" />
          <div className="text-center sm:text-left flex-1">
            <p className="font-bold text-gray-900 text-sm">Need shipping or transit help?</p>
            <p className="text-xs text-gray-500 mt-0.5 font-light">Call / WhatsApp Royal Pet Store at 0329 5025172</p>
          </div>
          <Link to="/contact" className="bg-accent hover:bg-accent/80 text-white px-5 py-2.5 text-xs font-bold uppercase tracking-widest rounded-lg transition-colors whitespace-nowrap">
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
}
